import { useState } from 'preact/hooks';
import { recordConsent } from '../api/settings';
import { getBrand, getBrandId } from '../brands';

interface Props {
  onAccepted: () => void;
}

/**
 * One-time consent modal shown to new users before they can use the app.
 * Requires accepting ToS + Privacy Policy. Agent Access Consent is a separate
 * checkbox shown inside — users only need it if they connect an SSH machine.
 *
 * Brand-aware (Protofac signup gate, Phase 3): on the `protofac` brand the modal also
 * presents the credit disclosure — the managed sandbox authenticates with Anthropic and
 * runs on the user's OWN Anthropic credits (invariant #4) — as a gated acknowledgement.
 *
 * Restyle (lane va-ui-mobile-token-unify-2026-10-01, issue #2203): the presentation moved
 * from inline style objects to the class-driven `.consent-modal*` stylesheet in style.css
 * so the first surface a mobile user sees speaks the SAME language as the desktop home
 * (lane va-ui-desktop-home, #2201): token-family chrome (--va-*, stamped on the shell
 * root), a real SVG hero mark (no emoji-as-icon), real <button>/<a>/<label> controls, and
 * DESIGNED CTA states exposed via data-cta-state (disabled → ready → saving) plus a
 * designed error state (role="alert", --va-danger). ZERO product delta: the consent
 * semantics — checkboxes, canProceed gating, the recordConsent payload, the Protofac
 * credit branch, the error copy — are byte-for-byte the pre-lane behavior.
 */
export function ConsentModal({ onAccepted }: Props) {
  const [tosChecked, setTosChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [creditConsent, setCreditConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const brandName = getBrand().name;
  const isProtofac = getBrandId() === 'protofac';

  // Protofac users must also acknowledge the credit disclosure before proceeding.
  const canProceed = tosChecked && privacyChecked && (!isProtofac || creditConsent);
  const ctaState = saving ? 'saving' : canProceed ? 'ready' : 'disabled';

  async function handleAccept() {
    if (!canProceed) return;
    setSaving(true);
    setError(null);
    try {
      await recordConsent({ tos: true, privacy: true, credit: isProtofac ? creditConsent : undefined });
      onAccepted();
    } catch (err) {
      setError('Failed to save your consent. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div class="consent-modal-backdrop">
      <div
        class="consent-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-modal-title"
      >
        {/* Voice hero language (the desktop-home idiom): a real SVG mic mark in an
            accent-tinted circle — no emoji-as-icon on this first-run surface. */}
        <div class="consent-modal-hero">
          <span class="consent-modal-hero-mark" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 3a3 3 0 0 1 3 3v5a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3Z" />
              <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
              <path d="M12 17.5V21" />
              <path d="M8.5 21h7" />
            </svg>
          </span>
          <h2 class="consent-modal-title" id="consent-modal-title">
            Welcome to {brandName}
          </h2>
          <p class="consent-modal-sub">
            Before you get started, please review and accept our terms.
          </p>
        </div>

        <div class="consent-modal-checks">
          <label class="consent-modal-check">
            <input
              type="checkbox"
              checked={tosChecked}
              onChange={(e) => setTosChecked((e.target as HTMLInputElement).checked)}
            />
            <span>
              I have read and accept the{' '}
              <a href="/legal/terms.html" target="_blank" rel="noopener noreferrer" class="consent-modal-link">
                Terms of Service
              </a>
            </span>
          </label>

          <label class="consent-modal-check">
            <input
              type="checkbox"
              checked={privacyChecked}
              onChange={(e) => setPrivacyChecked((e.target as HTMLInputElement).checked)}
            />
            <span>
              I have read and accept the{' '}
              <a href="/legal/privacy.html" target="_blank" rel="noopener noreferrer" class="consent-modal-link">
                Privacy Policy
              </a>
              {' '}including the use of sub-processors (Anthropic, Google, Stripe)
            </span>
          </label>
        </div>

        {isProtofac && (
          <div class="consent-modal-disclosure">
            <div class="consent-modal-disclosure-text">
              {/* Second-person disclosure — your managed sandbox runs on your own Anthropic credits. */}
              Your managed sandbox authenticates with Anthropic and runs on your own Anthropic
              account and credits. {brandName} does not resell AI usage — all model usage is billed
              directly to you by Anthropic.
            </div>
            <label class="consent-modal-check">
              <input
                type="checkbox"
                checked={creditConsent}
                onChange={(e) => setCreditConsent((e.target as HTMLInputElement).checked)}
              />
              <span>
                I understand that I must <strong>authenticate</strong> with Anthropic from within my
                own managed sandbox, and that all AI usage runs on my own Anthropic account and credits.
              </span>
            </label>
          </div>
        )}

        <div class="consent-modal-data">
          <strong>Data we collect:</strong> your Google profile, chat messages, and any API keys or GitHub tokens you choose to connect — all encrypted at rest. Voice audio is transcribed on your device and never stored. See the Privacy Policy for full details.
        </div>

        {error && (
          <div class="consent-modal-error" role="alert">
            {error}
          </div>
        )}

        <button
          type="button"
          class="consent-modal-cta"
          data-cta-state={ctaState}
          aria-busy={saving}
          onClick={handleAccept}
          disabled={!canProceed || saving}
        >
          {saving ? 'Saving…' : 'I agree — Get started'}
        </button>
      </div>
    </div>
  );
}
